import { NavLink, Link } from 'react-router-dom';
import { useIXVerifyAuth } from '../../contexts';
import type { IXVerifyRole } from '../../types';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  path: string;
  label: string;
  icon: string;
  roles?: IXVerifyRole[]; // If undefined, available to all roles
}

// Main navigation items - available to all authenticated users
const mainNavItems: NavItem[] = [
  {
    path: '/ixverify/dashboard',
    label: 'Dashboard',
    icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'
  },
  {
    path: '/ixverify/verify',
    label: 'New Verification',
    icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
  },
  {
    path: '/ixverify/history',
    label: 'History',
    icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
  },
  // TODO: Re-enable when shipment reconciliation feature is ready
  // {
  //   path: '/ixverify/shipment',
  //   label: 'Reconcile Shipment',
  //   icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4'
  // },
];

// Management section - coordinators and admins
const managementItems: NavItem[] = [
  {
    path: '/ixverify/recipients',
    label: 'Saved Recipients',
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
    roles: ['admin', 'coordinator']
  },
];

// Admin only items
const adminItems: NavItem[] = [
  {
    path: '/ixverify/users',
    label: 'User Management',
    icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
    roles: ['admin', 'superadmin']
  },
  {
    path: '/ixverify/studies',
    label: 'Study Management',
    icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
    roles: ['admin', 'superadmin']
  },
  {
    path: '/ixverify/audit-logs',
    label: 'Audit Logs',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
    roles: ['admin', 'superadmin']
  },
];

// Super Admin only items
const superAdminItems: NavItem[] = [
  {
    path: '/ixverify/superadmin',
    label: 'Super Admin',
    icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    roles: ['superadmin']
  },
];

// Settings - always at the bottom
const settingsItem: NavItem = {
  path: '/ixverify/settings',
  label: 'Settings',
  icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z'
};

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { user } = useIXVerifyAuth();
  const userRole = user?.ixverify_role || 'viewer';

  const canAccess = (item: NavItem) => {
    if (!item.roles) return true; // Available to all
    // Superadmin has access to everything except items explicitly restricted
    if (userRole === 'superadmin') return true;
    return item.roles.includes(userRole);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex min-h-[44px] items-center gap-3 rounded-lg px-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 lg:min-h-0 lg:py-1.5 ${
      isActive
        ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
    }`;

  const filteredMainItems = mainNavItems.filter(canAccess);
  const filteredManagementItems = managementItems.filter(canAccess);
  const filteredAdminItems = adminItems.filter(canAccess);
  const filteredSuperAdminItems = superAdminItems.filter(canAccess);

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-gray-200
          transform transition-transform duration-200 ease-in-out
          dark:bg-gray-800 dark:border-gray-700
          lg:translate-x-0 lg:z-auto lg:sticky lg:top-12 lg:h-[calc(100vh-3rem)] lg:self-start
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 lg:hidden">
          <div className="flex items-center gap-2">
            {/* Tool Hub Logo */}
            <Link to="/" onClick={onClose} className="hover:opacity-80 transition-opacity">
              <img
                src="/pilllogo192.png"
                alt="Tool Hub"
                className="h-6 w-auto"
              />
            </Link>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            {/* IX Verify Logo */}
            <Link to="/ixverify/dashboard" onClick={onClose} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">IX</span>
              </div>
              <span className="font-semibold text-gray-900 dark:text-gray-100">IX Verify</span>
            </Link>
          </div>
        </div>

        <nav className="p-3 flex flex-col overflow-y-auto h-[calc(100%-65px)] lg:h-full">
          {/* Main Navigation */}
          <div className="space-y-0.5">
            {filteredMainItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={linkClass}
                onClick={onClose}
              >
                <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                </svg>
                {item.label}
              </NavLink>
            ))}
          </div>

          {/* Management Section (Coordinators and Admins) */}
          {filteredManagementItems.length > 0 && (
            <div className="pt-3 mt-3 border-t border-gray-100 dark:border-gray-800">
              <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                Management
              </p>
              <div className="space-y-0.5">
                {filteredManagementItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={linkClass}
                    onClick={onClose}
                  >
                    <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          )}

          {/* Admin Section */}
          {filteredAdminItems.length > 0 && (
            <div className="pt-3 mt-3 border-t border-gray-100 dark:border-gray-800">
              <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                Admin
              </p>
              <div className="space-y-0.5">
                {filteredAdminItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={linkClass}
                    onClick={onClose}
                  >
                    <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          )}

          {/* Super Admin Section */}
          {filteredSuperAdminItems.length > 0 && (
            <div className="pt-3 mt-3 border-t border-gray-100 dark:border-gray-800">
              <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-red-500 dark:text-red-400">
                Super Admin
              </p>
              <div className="space-y-0.5">
                {filteredSuperAdminItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={linkClass}
                    onClick={onClose}
                  >
                    <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                    </svg>
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Settings at bottom */}
          <div className="pt-3 border-t border-gray-100 dark:border-gray-800">
            <NavLink
              to={settingsItem.path}
              className={linkClass}
              onClick={onClose}
            >
              <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={settingsItem.icon} />
              </svg>
              {settingsItem.label}
            </NavLink>

            {/* User info */}
            {user && (
              <div className="mt-3 px-3 py-2 bg-gray-50 rounded-lg dark:bg-gray-900/40">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center dark:bg-gray-700">
                    <span className="text-gray-600 text-sm font-medium dark:text-gray-300">
                      {(user.full_name || user.email).charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate dark:text-gray-100">
                      {user.full_name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 truncate dark:text-gray-400">{user.email}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>
      </aside>
    </>
  );
}
